import { Component, Fragment, h, Prop } from '@stencil/core';
import { Article, DetailsComponents, Picker } from '../../utils/types';
import { showTimePickers } from '../../utils/picker';
import { buttonAppearance, buttonControlValues } from '../../utils/utils';
import { ComponentView } from '../../../../types/types';
import { t } from '../../../../redux/utils';

@Component({
  tag: 'rtr-article-details-calendar',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RtrArticleDetailsCalendar {
  @Prop() article: Article;
  @Prop() picker: Picker;
  @Prop() detailsComponent: DetailsComponents;

  get showInCartButton(): boolean {
    if (this.article.variation) {
      return this.article.isSelectedVariationInCart;
    }

    return !!this.article.itemInCart;
  }

  get showNotAvailableOverlay(): boolean {
    const componentLoading = this.picker.isCalendarLoading || this.picker.loadingPickUpHours || this.picker.loadingDropOffHours;

    if (this.picker.isOrder) {
      return !this.article.available && !componentLoading;
    }

    const checkNotAvailable = () => {
      const dropOffTimeExist: boolean = this.picker.withTime && !!this.picker.dropOffTime;
      const dropOffDateExist: boolean = this.picker.withDateRange && !this.picker.withTime && !!this.picker.dropOffDate;
      return !this.article.available && (dropOffTimeExist || dropOffDateExist);
    };

    return checkNotAvailable() && !componentLoading;
  }

  render() {
    const timeHint = t('articleBooking.chooseHint')
    const pickUpLabel = t('articleBooking.pickUp')
    const dropOffLabel = t('articleBooking.dropOff')
    const timeSlotHint = t('articleBooking.timeSlotHint')
    const timeSlotLabel = t('articleBooking.timeSlotHint')

    return (
      <div class="ADCWrapper">
        <rentware-spacer size="S" />
        <div class="ADCInfo">
          <rtr-date-info pickerDate={this.picker} />

          {this.picker.isOrder && this.article.removable && (
            <div class="ADCButton">
              {!this.showInCartButton &&
                (((this.article.available || this.article.isWaiting) && (
                  <rentware-button
                    appearance={buttonAppearance(this.article)}
                    buttonText={this.detailsComponent.buttonText}
                    controlValues={buttonControlValues(this.article)}
                    onClick={this.detailsComponent.onAddToCart}
                    isWaiting={this.article.isWaiting}
                  />
                )) || <rtr-button-unavailable />)}

              {this.showInCartButton && (
                <rentware-amount-control
                  view={ComponentView.Details}
                  controlValues={{
                    disabled: this.article.isWaiting,
                    minAmount: this.article.minAmount,
                    maxAmount: this.article.amount + this.article.availableAmount,
                    isVariation: !!this.article.variation,
                    itemAmount: this.article.amount,
                    changeAmountCallback: this.article.onChangeAmount,
                    deleteArticleCallback: this.article.onDeleteArticle,
                  }}
                />
              )}
            </div>
          )}
        </div>

        <rentware-spacer size="M" />

        {!this.picker.isOrder && this.article.removable && (
          <Fragment>
            <rentware-range-picker
              numberOfMonths={2}
              locale={this.picker.locale}
              onRangeDayClick={this.picker.onRangeDayClick}
              onRangeMonthClick={this.picker.onRangeMonthClick}
              onResetCalendar={this.picker.onResetCalendar}
              withDateRange={this.picker.withDateRange}
              rangeLoading={this.picker.isCalendarLoading}
              daysAvailability={this.picker.daysAvailability}
              firstAvailableDate={this.picker.firstAvailableDate}
              bookingDate={this.picker.pickUpDate}
              endDate={this.picker.dropOffDate}
              monthAtStart={this.picker.pickUpDate}
              showNotAvailableOverlay={this.showNotAvailableOverlay}
            />

            <rentware-spacer size="L" />

            <div class="ADCPickersContainer">
              <div class="ADCPickers">
                {showTimePickers(this.picker) && (
                  <Fragment>
                    {showTimePickers(this.picker)}
                    <div class="ADCPicker">
                      <rentware-time-picker
                        autoClose={this.picker.withDateRange}
                        hint={timeHint}
                        label={pickUpLabel}
                        value={this.picker.pickUpTime}
                        options={this.picker.pickUpHours}
                        disabled={!this.picker.pickUpDate}
                        onTimeChange={this.picker.onPickUpTimeChange}
                        isLoading={this.picker.loadingPickUpHours}
                        isPlaceholderRed={true}
                      />
                    </div>

                    {!this.picker.timeSlotPicker && (
                      <div class="ADCPicker">
                        <rentware-time-picker
                          autoClose={true}
                          hint={timeHint}
                          label={dropOffLabel}
                          value={this.picker.dropOffTime}
                          options={this.picker.dropOffHours}
                          onTimeChange={this.picker.onDropOffTimeChange}
                          disabled={!this.picker.dropOffPickerAvailable}
                          isLoading={this.picker.loadingDropOffHours}
                          isPlaceholderRed={true}
                        />
                      </div>
                    )}
                  </Fragment>
                )}

                {this.picker.timeSlotPicker && (
                  <div class="ADCPicker">
                    <rentware-time-picker
                      autoClose={true}
                      hint={timeSlotHint}
                      label={timeSlotLabel}
                      value={this.picker.timeSlotPicker.selectedOption}
                      optionsWithLabel={this.picker.timeSlotPicker.options}
                      onTimeChange={this.picker.onTimeSlotChange}
                      disabled={!this.picker.pickUpDate}
                      isPlaceholderRed={true}
                    />
                  </div>
                )}
              </div>

              <div class="ADCButton">
                {((this.article.available || this.article.isWaiting || !this.picker.isDateReady) && (
                  <rentware-button
                    appearance={buttonAppearance(this.article)}
                    buttonText={this.detailsComponent.buttonText}
                    controlValues={buttonControlValues(this.article)}
                    onClick={this.detailsComponent.onAddToCart}
                    isWaiting={this.article.isWaiting}
                  />
                )) || <rtr-button-unavailable />}
              </div>
            </div>
          </Fragment>
        )}
      </div>
    );
  }
}
